import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Trophy, Clock, TrendingUp, Users, Zap, Shield, Star, Sparkles, ArrowRight, Smartphone } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { trackEvent } from "@/lib/analytics";

const Landing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('landing_page_viewed');
  }, []);
  return (
    <div className="min-h-screen bg-background charcoal-pro">
      <MarketingHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Brand tagline */}
          <p className="text-lg md:text-xl text-primary font-medium tracking-wide">
            Made for consistency. Built for enjoyment.
          </p>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
            Finally. Studying That{" "}
            <span className="text-primary">Doesn't Suck.</span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
            Transform boring study sessions into a game. Earn XP, maintain streaks, climb leaderboards — and actually enjoy the grind.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button size="lg" className="text-lg px-8" onClick={() => {
              trackEvent('hero_cta_clicked', { button: 'start_studying_free' });
              navigate('/auth');
            }}>
              Start Studying Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg" onClick={() => {
              trackEvent('hero_secondary_clicked', { button: 'see_features' });
              navigate('/features');
            }}>
              See Features
            </Button>
          </div>

          {/* Returning user link */}
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <button onClick={() => {
              trackEvent('hero_signin_clicked');
              navigate('/auth');
            }} className="text-primary hover:underline font-medium">
              Sign in
            </button>
          </p>
          
          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 text-primary" />
              100% free tier
            </span>
            <span>•</span>
            <span>No credit card required</span>
            <span>•</span>
            <span>Cancel anytime</span>
          </div>
        </div>

        {/* Hero Image */}
        <div className="mt-12 md:mt-16 max-w-5xl mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10 pointer-events-none" />
            <Card className="overflow-hidden border-2 border-border shadow-2xl">
              <img 
                src="/images/dashboard-hero.png" 
                alt="StudyForge Dashboard showing study analytics, streaks, and leaderboard rankings"
                className="w-full h-auto"
                onError={(e) => {
                  // Fallback to placeholder if image doesn't exist
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `
                    <div class="aspect-video bg-secondary/50 flex items-center justify-center p-8">
                      <div class="text-center space-y-4">
                        <div class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                          <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                        </div>
                        <p class="text-muted-foreground">Dashboard preview coming soon</p>
                      </div>
                    </div>
                  `;
                }}
              />
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-secondary/30 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Consistency meets enjoyment in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Start a Session</h3>
              <p className="text-muted-foreground">
                Choose a preset timer (30/45/60/90 min) or create your own. Hit start and focus.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Earn XP & Build Streaks</h3>
              <p className="text-muted-foreground">
                Every minute counts. Earn XP, extend your streak, and watch your prestige grow.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Climb the Leaderboard</h3>
              <p className="text-muted-foreground">
                Compete globally or with friends. See where you rank and push yourself further.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Everything You Need for Consistent, Enjoyable Study
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Built for students, professionals, and lifelong learners who want studying to actually feel rewarding.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Clock className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Smart Study Timer
            </h3>
            <p className="text-muted-foreground">
              Preset Forge Cycles (30/45/60/90 min) or create custom timers. Recommended break calculations included.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <TrendingUp className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Progress Tracking
            </h3>
            <p className="text-muted-foreground">
              Real-time streak calculation, daily/weekly/monthly analytics, and lifetime study graphs.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Trophy className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Global Leaderboards
            </h3>
            <p className="text-muted-foreground">
              Compete globally or with friends. Daily, weekly, monthly, and yearly rankings.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Zap className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              XP & Prestige System
            </h3>
            <p className="text-muted-foreground">
              Earn XP through study, unlock prestige tiers from Apprentice to Legendary Master.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Users className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Friends System
            </h3>
            <p className="text-muted-foreground">
              Follow friends, view their stats, and compete in private leaderboards.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow border-primary/50">
            <Shield className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Streak Protection
              <span className="ml-2 text-xs px-2 py-1 rounded bg-primary/10 text-primary">Premium</span>
            </h3>
            <p className="text-muted-foreground">
              2 freeze days per month. Never lose your hard-earned streak on busy days.
            </p>
          </Card>
        </div>

        <div className="text-center mt-10">
          <Button variant="outline" size="lg" onClick={() => {
            trackEvent('view_all_features_clicked');
            navigate('/features');
          }}>
            View All Features
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Early Adopters / Social Proof */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Built by a Student Who Uses It Daily
            </h2>
            <p className="text-xl text-muted-foreground">
              New features ship weekly based on real feedback. Join early and lock in your pricing before it goes up.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border">
                <Zap className="w-5 h-5 text-primary" />
                <span className="text-foreground font-medium">Free tier forever</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border">
                <Star className="w-5 h-5 text-primary" />
                <span className="text-foreground font-medium">Lock in your pricing today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App - System Expansion */}
      {/* FUTURE: When app store links are ready, convert AppStoreBadge divs to <a> tags */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center justify-center gap-2">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            One System. Every Screen.
          </h2>
          <p className="text-muted-foreground">
            Your progress, everywhere. Mobile apps in development.
          </p>
          
          {/* Platform Badges - Currently informational, convert to links when ready */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            {/* iOS Badge */}
            {/* FUTURE: Replace div with <a href="https://apps.apple.com/..." target="_blank" rel="noopener"> */}
            {/* FUTURE: Remove opacity-60, cursor-default, select-none and add hover:bg-secondary/50 */}
            <div 
              className="flex items-center gap-3 px-5 py-3 rounded-lg bg-secondary/30 
                        border border-border opacity-60 cursor-default select-none"
              aria-label="iOS app coming soon"
            >
              <svg className="w-7 h-7 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-muted-foreground">iOS</p>
                <p className="text-sm font-medium text-foreground">Coming Soon</p>
              </div>
            </div>
            
            {/* Android Badge */}
            {/* FUTURE: Replace div with <a href="https://play.google.com/..." target="_blank" rel="noopener"> */}
            {/* FUTURE: Remove opacity-60, cursor-default, select-none and add hover:bg-secondary/50 */}
            <div 
              className="flex items-center gap-3 px-5 py-3 rounded-lg bg-secondary/30 
                        border border-border opacity-60 cursor-default select-none"
              aria-label="Android app coming soon"
            >
              <svg className="w-7 h-7 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.51-.38-.83-.22-.3.16-.42.54-.26.85L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-muted-foreground">Android</p>
                <p className="text-sm font-medium text-foreground">Coming Soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16 md:py-20 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Ready for Study That's Built Different?
          </h2>
          <p className="text-xl text-muted-foreground">
            Made for consistency. Built for enjoyment. Start your journey today.
          </p>
          <Button size="lg" className="text-lg px-8" onClick={() => {
            trackEvent('footer_cta_clicked', { page: 'landing' });
            navigate('/auth');
          }}>
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-muted-foreground">
            Free tier includes all core features • Upgrade anytime
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Landing;
